import React, { useState, useEffect } from "react";
import { useTheme } from "../hooks/useTheme";
import AppSidebar from "./AppSidebar";
import { useNavigate, useLocation } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { FormInput, FormSelect, FormButton } from "./FormComponents";
import { getTodayString } from "../utils/dateUtils";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { createTodo } from "../store/slices/todosSlice";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import { useReadonlyMode } from "../utils/offlineUtils";
import { useToastContext } from "./ToastContext";

const AddTodo: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.todos);
  const userProfile = useAppSelector((state) => state.user.profile);
  const { isDark, setTheme } = useTheme();
  const initials = userProfile?.name
    ? userProfile.name
        .split(" ")
        .map((w: string) => w.charAt(0).toUpperCase())
        .slice(0, 2)
        .join("")
    : (userProfile?.username?.charAt(0).toUpperCase() ?? "U");

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];
  const isReadonly = useReadonlyMode();
  const { addToast } = useToastContext();

  // Get date from URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const prefilledDate = queryParams.get("date");

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    date: prefilledDate || getTodayString(),
    priority: "" as "low" | "medium" | "high" | "",
  });

  const priorityOptions = [
    { value: "low", label: t("addTodo.lowPriority") },
    { value: "medium", label: t("addTodo.mediumPriority") },
    { value: "high", label: t("addTodo.highPriority") },
  ];

  useEffect(() => {
    trackPageVisit("add_todo");
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      return;
    }

    try {
      // Dispatch createTodo action to Redux store
      const result = await dispatch(
        createTodo({
          title: formData.title.trim(),
          date: formData.date,
          priority: formData.priority || undefined,
        }),
      ).unwrap();

      // Show toast if points earned
      if (result.pointsEarned > 0) {
        addToast(
          t("toast.pointsEarned", { points: result.pointsEarned }),
          "success",
        );
      }

      // Track todo creation event
      trackUserInteraction(
        "create_todo",
        "todo_management",
        result?.id || "unknown",
      );

      // Navigate to todos page
      navigate("/todos");
    } catch (error) {
      console.error("Error saving todo:", error);
      // TODO: Show error message to user
    }
  };

  const handleCancel = () => {
    navigate("/todos");
  };

  return (
    <>
      <MetaHead
        title={`${t("addTodo.title")} - ${t("app.name")}`}
        description={t("addTodo.description") as string}
        keywords={t("addTodo.keywords") as string}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#3b5cde]/20 border border-[#3b5cde]/30 flex items-center justify-center transition-all hover:bg-[#3b5cde]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("addTodo.title")}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                aria-label="Go to profile"
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#3b5cde]/15 border border-[#3b5cde]/25 flex items-center justify-center transition-all hover:bg-[#3b5cde]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {t("addTodo.title")}
            </h1>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            {/* Form Content */}
            <div className="mx-auto pb-8">
              {/* Introduction Card */}
              <div className="mb-6 glass-card p-5 lg:p-6 rounded-xl border border-[#378ADD]/20 dark:border-[#378ADD]/30 bg-gradient-to-br from-[#378ADD]/[0.05] to-transparent dark:from-[#378ADD]/[0.08]">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#378ADD]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg
                      className="w-5 h-5 text-[#378ADD]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-[19px] font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
                      {t("addTodo.createNewTodo")}
                    </h2>
                    <p className="text-[17px] text-neutral-600 dark:text-neutral-400">
                      {t("addTodo.todoDescription")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Card */}
              <div className="rounded-xl">
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Title Input */}
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-[17px] font-medium text-neutral-900 dark:text-neutral-100 mb-2.5"
                    >
                      {t("addTodo.todoTitle")}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <FormInput
                      id="title"
                      label=""
                      value={formData.title}
                      onChange={(value) =>
                        setFormData({ ...formData, title: value })
                      }
                      placeholder={t("addTodo.placeholder") as string}
                    />
                    <p className="text-[15px] text-neutral-500 dark:text-neutral-400 mt-1.5">
                      {formData.title.length}/100 characters
                    </p>
                  </div>

                  {/* Date and Priority Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Date Input */}
                    <div>
                      <label
                        htmlFor="date"
                        className="block text-[17px] font-medium text-neutral-900 dark:text-neutral-100 mb-2.5"
                      >
                        {t("addTodo.dueDate")}
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <FormInput
                        id="date"
                        label=""
                        type="date"
                        value={formData.date}
                        onChange={(value) =>
                          setFormData({ ...formData, date: value })
                        }
                      />
                    </div>

                    {/* Priority Select */}
                    <div>
                      <label
                        htmlFor="priority"
                        className="block text-[17px] font-medium text-neutral-900 dark:text-neutral-100 mb-2.5"
                      >
                        {t("addTodo.priority")}
                      </label>
                      <FormSelect
                        id="priority"
                        label=""
                        value={formData.priority}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            priority: value as "low" | "medium" | "high",
                          })
                        }
                        options={priorityOptions}
                        placeholder={t("addTodo.selectPriority") as string}
                      />
                    </div>
                  </div>

                  {/* Priority Info */}
                  {formData.priority && (
                    <div className="p-3.5 bg-[#378ADD]/[0.06] dark:bg-[#378ADD]/[0.08] border border-[#378ADD]/[0.15] dark:border-[#378ADD]/[0.2] rounded-lg">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#378ADD] mt-1.5 flex-shrink-0"></div>
                        <p className="text-[16px] text-neutral-700 dark:text-neutral-300">
                          {formData.priority === "high" &&
                            t("addTodo.highPriorityDesc")}
                          {formData.priority === "medium" &&
                            t("addTodo.mediumPriorityDesc")}
                          {formData.priority === "low" &&
                            t("addTodo.lowPriorityDesc")}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                    <div className="flex-1">
                      <FormButton
                        type="button"
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={loading}
                        size="lg"
                      >
                        {t("addTodo.cancel")}
                      </FormButton>
                    </div>

                    <div className="flex-1">
                      <FormButton
                        type="submit"
                        variant="primary"
                        loading={loading}
                        disabled={!formData.title.trim() || isReadonly}
                        size="lg"
                      >
                        {loading
                          ? t("addTodo.saving")
                          : isReadonly
                            ? t("addTodo.offline")
                            : t("addTodo.saveTodo")}
                      </FormButton>
                    </div>
                  </div>
                </form>
              </div>

              {/* Help Section */}
              <div className="mt-8 p-5 lg:p-6 glass-card rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#378ADD]/[0.15] flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-[#378ADD]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[17px] font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
                      {t("addTodo.helpTitle")}
                    </h3>
                    <p className="text-[16px] text-neutral-600 dark:text-neutral-400 leading-relaxed">
                      {t("addTodo.helpText")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* MOBILE BOTTOM NAV */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 backdrop-blur-sm border-t border-black/[0.08] dark:border-white/[0.08]">
          <div className="flex items-center justify-around px-2 pt-2 pb-2">
            {mobileNavLinks.map(({ path, emoji, label }) => {
              const active = isNavActive(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex flex-col items-center gap-1 cursor-pointer"
                  style={{ color: active ? "#f5a623" : undefined }}
                >
                  <span className="text-[22px]">{emoji}</span>
                  <span
                    className={`text-[13px] font-medium${active ? "" : " text-gray-400 dark:text-white/25"}`}
                    style={active ? { color: "#f5a623" } : undefined}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default AddTodo;
